'use client';

import { useTranslations } from 'next-intl';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Instrument, Polya, useData, useStrana } from './obshchee';

/**
 * DNS, WHOIS и SSL: три страницы, три инструмента, общий вид ответа.
 */

interface Zapis {
  tip: string;
  znachenie: string;
  prioritet?: number;
}

interface OtvetDns {
  domen: string;
  zapisi: Zapis[];
  netDomena: boolean;
}

export function DnsLookup() {
  const t = useTranslations('dh_set_ui');
  return (
    <Instrument<OtvetDns>
      instrument="dns"
      placeholder={t('placeholder_domen')}
      primery={['google.com', 'wikipedia.org', 'github.com']}
      pokazat={(d) =>
        d.netDomena ? (
          <p className="rounded-2xl border border-[var(--color-border)] p-5 text-sm">
            {t('dns_net_domena', { domen: d.domen })}
          </p>
        ) : d.zapisi.length === 0 ? (
          <p className="rounded-2xl border border-[var(--color-border)] p-5 text-sm">
            {t('dns_pusto')}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs text-[var(--color-text-muted)]">
                  <th className="px-4 py-3 font-medium">{t('dns_tip')}</th>
                  <th className="px-4 py-3 font-medium">{t('dns_znachenie')}</th>
                </tr>
              </thead>
              <tbody>
                {d.zapisi.map((z, i) => (
                  <tr
                    key={`${z.tip}-${String(i)}`}
                    className="border-b border-[var(--color-border)] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 align-top font-mono text-xs font-semibold">
                      {z.tip}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs [overflow-wrap:anywhere]">
                      {z.prioritet !== undefined ? (
                        <span className="mr-2 text-[var(--color-text-muted)]">{z.prioritet}</span>
                      ) : null}
                      {z.znachenie}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    />
  );
}

interface OtvetWhois {
  vid: 'domen' | 'ip';
  domen?: string;
  vvedeno?: string;
  istochnik?: 'rdap' | 'whois';
  server: string;
  svoboden?: boolean;
  registrator?: string;
  registratorUrl?: string;
  sozdan?: string;
  istekaet?: string;
  obnovlen?: string;
  statusy?: string[];
  ns?: string[];
  dnssec?: boolean;
  ip?: string;
  set?: string;
  diapazon?: string;
  nazvanie?: string;
  organizaciya?: string;
  strana?: string;
}

export function WhoisLookup() {
  const t = useTranslations('dh_set_ui');
  const data = useData();
  const strana = useStrana();
  return (
    <Instrument<OtvetWhois>
      instrument="whois"
      placeholder={t('placeholder_whois')}
      primery={['google.com', 'bbc.co.uk', 'rnids.rs', '8.8.8.8']}
      pokazat={(d) => (
        <div className="space-y-3">
          {d.vvedeno ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              {t('whois_registriruemyj', { vvedeno: d.vvedeno, domen: d.domen ?? '' })}
            </p>
          ) : null}
          {d.vid === 'domen' && d.svoboden ? (
            <p className="rounded-2xl border border-[var(--color-success-500)]/40 p-5 text-sm">
              {t('whois_svoboden', { domen: d.domen ?? '' })}
            </p>
          ) : d.vid === 'domen' ? (
            <Polya
              stroki={[
                [
                  t('pole_domen'),
                  <span key="d" className="font-mono">
                    {d.domen}
                  </span>,
                ],
                [
                  t('pole_registrator'),
                  d.registratorUrl ? (
                    <a
                      key="r"
                      href={d.registratorUrl}
                      target="_blank"
                      rel="noopener nofollow"
                      className="underline underline-offset-2"
                    >
                      {d.registrator ?? d.registratorUrl}
                    </a>
                  ) : (
                    d.registrator
                  ),
                ],
                [t('pole_sozdan'), data(d.sozdan)],
                [t('pole_istekaet'), data(d.istekaet)],
                [t('pole_obnovlen'), data(d.obnovlen)],
                [t('pole_statusy'), (d.statusy ?? []).join(', ')],
                [
                  t('pole_ns'),
                  <span key="ns" className="font-mono">
                    {(d.ns ?? []).join(', ')}
                  </span>,
                ],
                [t('pole_dnssec'), d.dnssec === undefined ? '' : d.dnssec ? t('da') : t('net')],
              ]}
            />
          ) : (
            <Polya
              stroki={[
                [
                  t('pole_ip'),
                  <span key="ip" className="font-mono">
                    {d.ip}
                  </span>,
                ],
                [t('pole_set'), [d.nazvanie, d.set].filter(Boolean).join(' · ')],
                [
                  t('pole_diapazon'),
                  <span key="dz" className="font-mono">
                    {d.diapazon}
                  </span>,
                ],
                [t('pole_organizaciya'), d.organizaciya],
                [t('pole_strana_seti'), strana(d.strana)],
                [t('pole_sozdan'), data(d.sozdan)],
                [t('pole_obnovlen'), data(d.obnovlen)],
              ]}
            />
          )}
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('whois_istochnik', {
              protokol: d.istochnik === 'whois' ? 'WHOIS' : 'RDAP',
              server: d.server,
            })}{' '}
            {t('whois_bez_pd')}
          </p>
        </div>
      )}
    />
  );
}

interface Sert {
  komu: string;
  kem: string;
  izdatel?: string;
  s: string;
  po: string;
}

interface OtvetSsl {
  domen: string;
  ip: string;
  doveryaem: boolean;
  oshibka?: string;
  imyaSovpadaet: boolean;
  protokol?: string;
  shifr?: string;
  list: Sert & { imena: string[]; dnejOstalos: number; serijnyj: string };
  cepochka: Sert[];
}

/** Коды Node → наши подписи. Незнакомый код показываем как есть. */
const OSHIBKI_SSL = new Set([
  'CERT_HAS_EXPIRED',
  'ERR_TLS_CERT_ALTNAME_INVALID',
  'DEPTH_ZERO_SELF_SIGNED_CERT',
  'SELF_SIGNED_CERT_IN_CHAIN',
  'UNABLE_TO_VERIFY_LEAF_SIGNATURE',
  'UNABLE_TO_GET_ISSUER_CERT_LOCALLY',
  'CERT_NOT_YET_VALID',
]);

export function SslChecker() {
  const t = useTranslations('dh_set_ui');
  const data = useData();
  return (
    <Instrument<OtvetSsl>
      instrument="ssl"
      placeholder={t('placeholder_domen')}
      primery={['google.com', 'expired.badssl.com', 'wrong.host.badssl.com']}
      pokazat={(d) => (
        <div className="space-y-4">
          <div
            className={`flex items-start gap-3 rounded-2xl border p-5 ${
              d.doveryaem
                ? 'border-[var(--color-success-500)]/40'
                : 'border-[var(--color-danger-500)]/50'
            }`}
          >
            {d.doveryaem ? (
              <CheckCircle2
                className="mt-0.5 h-6 w-6 shrink-0 text-[var(--color-success-400)]"
                aria-hidden
              />
            ) : (
              <XCircle
                className="mt-0.5 h-6 w-6 shrink-0 text-[var(--color-danger-400)]"
                aria-hidden
              />
            )}
            <div>
              <p className="font-semibold">
                {d.doveryaem ? t('ssl_ok', { domen: d.domen }) : t('ssl_ploho', { domen: d.domen })}
              </p>
              <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                {d.oshibka
                  ? OSHIBKI_SSL.has(d.oshibka)
                    ? t(`ssl_${d.oshibka}`)
                    : d.oshibka
                  : d.list.dnejOstalos < 15
                    ? t('ssl_skoro', { dni: d.list.dnejOstalos })
                    : t('ssl_dnej', { dni: d.list.dnejOstalos })}
              </p>
            </div>
          </div>
          <Polya
            stroki={[
              [
                t('pole_komu'),
                <span key="k" className="font-mono">
                  {d.list.komu}
                </span>,
              ],
              [t('pole_izdatel'), [d.list.izdatel, d.list.kem].filter(Boolean).join(' · ')],
              [t('pole_deystvuet'), `${data(d.list.s)} — ${data(d.list.po)}`],
              [
                t('pole_imena'),
                <span key="i" className="font-mono text-xs">
                  {d.list.imena.join(', ')}
                </span>,
              ],
              [t('pole_protokol'), [d.protokol, d.shifr].filter(Boolean).join(' · ')],
              [
                t('pole_ip'),
                <span key="ip" className="font-mono">
                  {d.ip}
                </span>,
              ],
              [
                t('pole_serijnyj'),
                <span key="s" className="font-mono text-xs">
                  {d.list.serijnyj}
                </span>,
              ],
            ]}
          />
          {d.cepochka.length > 0 ? (
            <div>
              <h3 className="mb-2 text-sm font-semibold">{t('ssl_cepochka')}</h3>
              <ol className="space-y-2 text-sm">
                {d.cepochka.map((c, i) => (
                  <li
                    key={`${c.komu}-${String(i)}`}
                    className="rounded-xl border border-[var(--color-border)] px-4 py-3"
                  >
                    <span className="font-medium">{c.komu}</span>
                    <span className="text-[var(--color-text-muted)]">
                      {' '}
                      · {t('ssl_vydal', { kem: c.kem })} · {t('ssl_do', { data: data(c.po) })}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      )}
    />
  );
}
